/** @jsx jsx */
import { jsx } from '@emotion/core';

import React, { useState } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import {
  Box,
  Divider,
  FormHelperText,
  Grid,
  Typography,
  useTheme,
} from '@material-ui/core';

import colors from '../../../../../themes/main-colors';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import CurrencyTextField from '../../../../currency-text-field';
import { ChargerInstallationCost } from '../rebateOnlyForm.types';
import {
  getConfirmationEvRebate,
  getPreRebateEligibilityConfirmationState,
  isVehiclePathCustomer,
} from '../rebateOnlyForm.utils';
import useFormState from '../../../../../hooks/useFormState';
import { createChargerInstallationCostValidateFunction } from '../rebateOnlyForm.rules';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import routes from '../../../../../routes';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { MultiFileUpload } from '../../../../pge-plus-common/multi-upload';
import Alerts from '../../../../alerts';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useUtil from '../../../../../hooks/pge-plus/useUtil';

const ChargerAndInstallationCosts: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const theme = useTheme();

  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(2),
  };

  const boldText = {
    fontWeight: 700,
  };

  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHARGER_AND_INSTALLATION_COST,
  );
  const { content: contentMCG } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG,
  );

  const { wrapWithLoader } = useWrapWithLoader();
  const {
    formState: { application, rebateConfirm, rebateInfo },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);

  // CUP-24444: Drives the Connect Vehicle vs Connect Charger routing below
  const isVehiclePath = isVehiclePathCustomer(rebateInfo);

  const chargerInstallation: ChargerInstallationCost = {
    // Note: ?? instead of ||.. a real $0 cost was showing as blank when resuming a saved application
    installationCost: String(
      rebateConfirm.chargerInstallInfo?.installationCost ?? '',
    ),
    chargerCost: String(rebateConfirm.chargerInstallInfo?.chargerCost ?? ''),
    files: rebateConfirm.chargerInstallInfo?.receiptImages ?? undefined,
  };
  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { setErrorNotification } = useUtil();

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const form = useFormState(chargerInstallation, {
    validate: createChargerInstallationCostValidateFunction(),
    validationContext: {
      content,
    },
  });

  const handleSubmit = wrapWithLoader(async (data: ChargerInstallationCost) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerInstallInfo: {
              ...rebateConfirm.chargerInstallInfo,
              installationCost: Number(data.installationCost) || 0,
              chargerCost: Number(data.chargerCost) || 0,
              receiptImages: data?.files,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
      );

      if (!response.data?.updateSectionERRebateConfirm.section.errors?.length) {
        if (response.data?.updateSectionERRebateConfirm?.section.data) {
          const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
            response.data?.updateSectionERRebateConfirm?.section.data,
          );
          updateFormState('rebateConfirm', {
            ...updatedValues,
          });
        }
        const responseData =
          response.data?.updateSectionERRebateConfirm?.section?.data;
        void navigate(routes.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION, {
          state: {
            ...getPreRebateEligibilityConfirmationState(
              getConfirmationEvRebate(
                responseData?.preRebateEligibility2,
                responseData?.preRebateEligibility1,
                responseData?.rebateConfirmInput?.chargerInstallInfo
                  ?.isChargerPurchased,
              ),
              false,
              isVehiclePath,
              responseData?.rebateInfo?.existingEnrollments?.iqbd,
              responseData?.rebateConfirmInput?.iqbdQualification
                ?.checkEligibility,
            ),
            // CUP-24444: The vehicle-path users now land on the new Connect Vehicle screen here instead of Connect Charger/Tesla
            nextButtonRoute: isVehiclePath
              ? routes.PGE_PLUS_REBATE_ONLY_CONNECT_VEHICLE
              : routes.PGE_PLUS_REBATE_ONLY_CONNECT_CHARGER,
          },
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      console.log('error', error);
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerInstallInfo: {
              ...rebateConfirm.chargerInstallInfo,
              installationCost: Number(form.values.installationCost) || 0,
              chargerCost: Number(form.values.chargerCost) || 0,
              receiptImages: form.values?.files,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Box>
      <form
        onSubmit={form.submit(values => handleSubmit(values), console.log)}
        noValidate
      >
        <Box
          css={{
            '& .MuiFormControl-root': {
              background: 'transparent',
            },
            '& .MuiInputBase-input': {
              background: '#FFF',
            },
          }}
        >
          <Typography css={{ ...subText1, ...boldText }}>
            {content.get('PLEASE_PROVIDE_CHARGER_INSTALLATION_COSTS_SUBHEADER')}
          </Typography>

          <Grid container spacing={2} css={{ justifyContent: 'space-between' }}>
            <Grid item xs={12} md={6}>
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    height: '20px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={content.get('CHARGER_COST') || ''}
                {...form.props('chargerCost')}
                error={Boolean(form.errors.chargerCost)}
                helperText={form.errors.chargerCost}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CurrencyTextField
                css={{
                  margin: 0,
                  width: '100%',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  marginTop: theme.spacing(3),
                }}
                label={content.get('INSTALLATION_COST') || ''}
                {...form.props('installationCost')}
                error={Boolean(form.errors.installationCost)}
                helperText={form.errors.installationCost}
              />
            </Grid>
          </Grid>
          <Divider css={{ color: colors.gray93, margin: '20px 0 20px 0' }} />
          <Typography css={{ ...subText1, ...boldText }}>
            {content.get('PROVIDE_COPY_RECEIPTS_TITLE')}
          </Typography>
          <Typography css={{ ...subText1 }}>
            {content.get('UPLOAD_RECEIPTS_DETAILS')}
          </Typography>
          <Box
            css={{
              marginBottom: theme.spacing(2),
              '& .MuiAlertTitle-root': { display: 'none' },
            }}
          >
            <Alerts
              isOpen
              severity={'info'}
              variant={'standard'}
              disableColorOverride
              message={contentMCG.get(
                'PGE_PLUS_EV_REBATES_FLOW_ELECTRICIAN_LICENSE_NUMBER_NOTE',
              )}
            />
          </Box>
          <MultiFileUpload
            form={form}
            formField={'files'}
            setIsFileUploading={setIsFileUploading}
          />
          {form.errors.files && (
            <FormHelperText error>{form.errors.files}</FormHelperText>
          )}
        </Box>
        <Divider css={{ color: colors.gray93, margin: '20px 0 20px 0' }} />
        <ProceedOrCancelButton
          back={routes.PGE_PLUS_REBATE_ONLY_CHARGER_INFORMATION}
          backLabel={content.get('BACK')}
          disable={
            isFileUploading ||
            !form.values.chargerCost ||
            !form.values.installationCost ||
            form.values.files === undefined ||
            form.values.files.length === 0
          }
        />
        <Box mt={6}>
          <SaveAndComeBackLink
            content={content}
            onSaveAndExit={() => handleSaveAndExit()}
          />
        </Box>
      </form>
    </Box>
  );
};

export default ChargerAndInstallationCosts;
